import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  CertificateListAction,
  DeleteCertificateAction,
} from "../../Redux/Actions/RewardAction";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditCertificateModal from "./EditCertificateModal";
import CertificateCriteriaModal from "./CertificateCriteriaModal";
import { ApiConfig } from "../../Config/CommonConfig";

const CertificatesTab = () => {
  const dispatch = useDispatch<any>();
  const { data: certificateData, loading } = useSelector(
    (state: any) => state?.CertificateListData
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [criteriaModalOpen, setCriteriaModalOpen] = useState(false);
  const [selectedCertificateForCriteria, setSelectedCertificateForCriteria] =
    useState<any>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const certificates = certificateData?.rows || [];
  const totalCount = certificateData?.count || 0;

  const refreshCertificateList = () => {
    const param = `?page=${page}&page_size=${rowsPerPage}`;
    dispatch(CertificateListAction(param));
  };

  useEffect(() => {
    refreshCertificateList();
  }, [dispatch, page, rowsPerPage]);

  const handleEdit = (certificate: any) => {
    setSelectedCertificate(certificate);
    setEditModalOpen(true);
  };

  const handleDelete = async (certificateId: string) => {
    if (window.confirm("Are you sure you want to delete this certificate?")) {
      const result = await dispatch(DeleteCertificateAction(certificateId));
      if (result && result.status === true) {
        refreshCertificateList(); 
      }
    }
  };

  const handlePreview = (previewUrl: string) => {
    window.open(previewUrl, "_blank");
  };

  const handleViewCriteria = (certificate: any) => {
    setSelectedCertificateForCriteria(certificate);
    setCriteriaModalOpen(true);
  };

  const calculateTotalPages = (count: number, pageSize: number) => {
    return Math.ceil(count / pageSize);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 1000 }}>
        <Table aria-label="Certificates Table">
          <TableHead sx={{ backgroundColor: "#0555c8" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Preview</TableCell>
              <TableCell sx={{ color: "white" }}>Name</TableCell>
              <TableCell sx={{ color: "white" }}>Code</TableCell>
              <TableCell sx={{ color: "white" }}>Organization</TableCell>
              <TableCell sx={{ color: "white" }}>Status</TableCell>
              <TableCell sx={{ color: "white" }}>Description</TableCell>
              <TableCell sx={{ color: "white" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {certificates?.length > 0 ? (
              certificates?.map((cert: any) => (
                <TableRow key={cert?.id}>
                  <TableCell>
                    {cert?.preview_url && (
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(cert?.preview_url)}
                        sx={{ color: "#0555c8" }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{cert?.name}</TableCell>
                  <TableCell>{cert?.code}</TableCell>
                  <TableCell>{cert?.organization}</TableCell>
                  <TableCell>
                    <Chip
                      label={cert?.is_active ? "Active" : "Inactive"}
                      color={cert?.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{cert?.description}</TableCell>
                  <TableCell>
                    <Tooltip title="View Criteria">
                      <IconButton
                        size="small"
                        onClick={() => handleViewCriteria(cert)}
                        sx={{ color: "#0555c8" }}
                      >
                        <ListAltIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(cert)}
                        sx={{ color: "#0555c8" }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(cert?.id)}
                        sx={{ color: "#d32f2f" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Certificates Found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ m: 2, display: "flex", justifyContent: "center" }}>
        <Stack spacing={2}>
          <Pagination
            count={calculateTotalPages(totalCount, rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      </Box>

      {editModalOpen && selectedCertificate && (
        <EditCertificateModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedCertificate(null);
          }}
          certificate={selectedCertificate}
          onSuccess={refreshCertificateList}
        />
      )}

      {criteriaModalOpen && selectedCertificateForCriteria && (
        <CertificateCriteriaModal
          open={criteriaModalOpen}
          onClose={() => {
            setCriteriaModalOpen(false);
            setSelectedCertificateForCriteria(null);
          }}
          certificate={selectedCertificateForCriteria}
        />
      )}
    </>
  );
};

export default CertificatesTab;
